import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import CollaborationIcon from "./CollaborationIcon";
import {
  formatArtifactDate,
  formatArtifactStatus,
  formatArtifactType,
  formatRelativeTime,
  getOwnerProfile
} from "../collaborationUtils";

const HeroCard = ({ artifact, onClick }) => {
  if (!artifact) {
    return null;
  }

  const owner = getOwnerProfile(artifact.owner);
  const relativeTime = formatRelativeTime(artifact.publishedAt);
  const dateOpen =
    artifact.nextStep?.dateOpen ||
    !artifact.date ||
    artifact.date === "1000-01-01" ||
    String(artifact.date).startsWith("1000-01-01");
  const metaSecondary = dateOpen
    ? "Datum offen"
    : relativeTime || formatArtifactDate(artifact.date);

  return (
    <Card type="button" onClick={onClick} disabled={!onClick}>
      <TopRow>
        <TypeBadge>{formatArtifactType(artifact.type)}</TypeBadge>
        <StatusPill>{formatArtifactStatus(artifact.status)}</StatusPill>
        {artifact.location && (
          <ContextLabel>
            <CollaborationIcon name="map-pin" size={12} />
            {artifact.location}
          </ContextLabel>
        )}
      </TopRow>
      <TitleLine>{artifact.title}</TitleLine>
      <MetaRow>
        <MetaAvatar>{owner.initials}</MetaAvatar>
        <span>{owner.fullName}</span>
        {metaSecondary && (
          <>
            <Dot>·</Dot>
            <span>{metaSecondary}</span>
          </>
        )}
      </MetaRow>
      {artifact.lead && <Lead>{artifact.lead}</Lead>}
      {artifact.nextStep?.what && (
        <NextStep>
          <NextStepIcon>
            <CollaborationIcon name="arrow-right" size={16} />
          </NextStepIcon>
          <NextStepBody>
            <NextStepLabel>Nächster Schritt</NextStepLabel>
            <NextStepWhat>{artifact.nextStep.what}</NextStepWhat>
            {artifact.nextStep.detail && (
              <NextStepDetail>{artifact.nextStep.detail}</NextStepDetail>
            )}
          </NextStepBody>
        </NextStep>
      )}
    </Card>
  );
};

HeroCard.propTypes = {
  artifact: PropTypes.shape({
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
    status: PropTypes.string,
    date: PropTypes.string,
    location: PropTypes.string,
    owner: PropTypes.string,
    lead: PropTypes.string,
    publishedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    nextStep: PropTypes.shape({
      what: PropTypes.string,
      detail: PropTypes.string
    })
  }),
  onClick: PropTypes.func
};

export default HeroCard;

const Card = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 22px 24px 20px;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  transition: background 120ms ease, border-color 120ms ease;

  &:hover:not(:disabled) {
    background: #fdfdfb;
    border-color: rgba(0, 0, 0, 0.12);
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const TypeBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: #e6f5ef;
  color: #0a7c4e;
`;

const StatusPill = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
`;

const ContextLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 999px;
  background: #f0eee8;
  color: #6a6960;

  svg {
    opacity: 0.7;
  }
`;

const TitleLine = styled.h2`
  margin: 0 0 8px;
  font-size: 21px;
  font-weight: 700;
  color: #181714;
  line-height: 1.3;
  letter-spacing: -0.005em;
  max-width: 720px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6a6960;
  margin-bottom: 14px;
`;

const MetaAvatar = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e2dfd8;
  color: #5f5e5a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  flex-shrink: 0;
`;

const Dot = styled.span`
  color: #c8c5bc;
`;

const Lead = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  color: #2c2b27;
  line-height: 1.55;
  max-width: 640px;
`;

const NextStep = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #faf9f5;
  border-radius: 6px;
  font-size: 13px;
  color: #2c2b27;
`;

const NextStepIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e6f5ef;
  color: #0a7c4e;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NextStepBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const NextStepLabel = styled.div`
  font-size: 10px;
  color: #a09e96;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  margin-bottom: 2px;
`;

const NextStepWhat = styled.div`
  font-weight: 600;
`;

const NextStepDetail = styled.div`
  font-size: 12px;
  color: #6a6960;
  margin-top: 1px;
`;
